import React, { useMemo, useState } from 'react';
import { Button, Descriptions, Input, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useQuery, useQueryClient } from 'react-query';
import dayjs from 'dayjs';
import { request } from '../../utils/axios-utils';
import { useGetCompanyListMinimal } from '../../hooks/useCompanies';
import { parseSkuSearchList } from '../../utils/parseSkuSearchList';

const PAGE_SIZE = 20;
const FLAG_FILTERS = [
    ['isConsumed', 'Consumed'],
    ['isArticleUpdated', 'Article updated'],
    ['useForMapping', 'Use for mapping'],
    ['hasProductVariant', 'Product variant'],
];
const CSV_TYPES = ['inbound', 'outbound', 'Order'];
const YES_NO = [
    { label: 'Yes', value: true },
    { label: 'No', value: false },
];

function toEditorValue(value) {
    if (value == null) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
}

function fromEditorValue(text, original) {
    if (typeof original === 'number' && text.trim() !== '' && !Number.isNaN(Number(text))) return Number(text);
    if (typeof original === 'boolean') return text === 'true';
    if (original && typeof original === 'object') {
        try {
            return JSON.parse(text);
        } catch (err) {
            return text;
        }
    }
    return text;
}

function toRows(obj) {
    return Object.entries(obj || {}).map(([key, value]) => ({ key, value: toEditorValue(value), original: value }));
}

function fromRows(rows) {
    const obj = {};
    for (const row of rows) {
        const key = String(row.key || '').trim();
        if (!key) continue;
        obj[key] = fromEditorValue(row.value, row.original);
    }
    return obj;
}

async function readError(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
        try {
            const json = JSON.parse(await data.text());
            return json.message || fallback;
        } catch (parseErr) {
            return fallback;
        }
    }
    return data?.message || err?.message || fallback;
}

function searchOrderCsv(payload) {
    return request({ url: '/csvdata/search', method: 'POST', data: payload });
}

function flagTag(value, yes, no) {
    return value ? <Tag color="green">{yes}</Tag> : <Tag>{no}</Tag>;
}

function KeyValues({ title, obj }) {
    const entries = Object.entries(obj || {});
    if (!entries.length) return null;
    return (
        <div style={{ marginTop: 12 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{title}</div>
            <Descriptions size="small" bordered column={2}>
                {entries.map(([key, value]) => (
                    <Descriptions.Item key={key} label={key}>
                        {value != null && typeof value === 'object' ? JSON.stringify(value) : String(value ?? '')}
                    </Descriptions.Item>
                ))}
            </Descriptions>
        </div>
    );
}

function RowEditor({ rows, onChange, lockKeys = [] }) {
    const locked = new Set(lockKeys);
    const update = (index, patch) => {
        const next = rows.map((row, i) => (i === index ? { ...row, ...patch } : row));
        onChange(next);
    };
    return (
        <div>
            {rows.map((row, index) => (
                <Space key={`${row.key}-${index}`} style={{ display: 'flex', marginBottom: 8 }} align="start">
                    <Input
                        value={row.key}
                        disabled={locked.has(row.key)}
                        placeholder="Field"
                        style={{ width: 220 }}
                        onChange={e => update(index, { key: e.target.value, original: undefined })}
                    />
                    <Input.TextArea
                        value={row.value}
                        autoSize={{ minRows: 1, maxRows: 4 }}
                        placeholder="Value"
                        style={{ width: 360 }}
                        onChange={e => update(index, { value: e.target.value })}
                    />
                    {locked.has(row.key) ? null : (
                        <Button
                            onClick={() => onChange(rows.filter((_, i) => i !== index))}
                        >
                            Remove
                        </Button>
                    )}
                </Space>
            ))}
            <Button onClick={() => onChange([...rows, { key: '', value: '', original: undefined }])}>Add field</Button>
        </div>
    );
}

export default function OrderCsvDataPage() {
    const queryClient = useQueryClient();
    const { data: companies, isFetching: companiesLoading } = useGetCompanyListMinimal();
    const [barcodeText, setBarcodeText] = useState('');
    const [companyCode, setCompanyCode] = useState();
    const [orderId, setOrderId] = useState();
    const [flags, setFlags] = useState({});
    const [csvType, setCsvType] = useState();
    const [submitted, setSubmitted] = useState(null);
    const [searchTick, setSearchTick] = useState(0);
    const [pageNum, setPageNum] = useState(1);
    const [exporting, setExporting] = useState(false);
    const [editing, setEditing] = useState(null);
    const [mappingRows, setMappingRows] = useState([]);
    const [propertyRows, setPropertyRows] = useState([]);
    const [editFlags, setEditFlags] = useState({ useForMapping: true, isArticleUpdated: false, csvType: 'inbound' });
    const [saving, setSaving] = useState(false);

    const parsed = parseSkuSearchList(barcodeText, { max: 2000 });
    const { data: orders, isFetching: ordersLoading } = useQuery(
        ['orderListMinimal', companyCode],
        async () => {
            const res = await request({
                url: '/order/orderListMinimal',
                method: 'GET',
                params: { companyCode },
            });
            return res?.data?.data || [];
        },
        { enabled: !!companyCode, refetchOnWindowFocus: false }
    );

    const payload = useMemo(() => {
        if (!submitted) return null;
        return { ...submitted, pageNum, pageSize: PAGE_SIZE };
    }, [submitted, pageNum]);

    const { data: result, isFetching } = useQuery(
        ['orderCsvSearch', payload, searchTick],
        async () => {
            const res = await searchOrderCsv(payload);
            return res?.data;
        },
        {
            enabled: !!payload,
            refetchOnWindowFocus: false,
            keepPreviousData: true,
            onError: err => message.error(err?.response?.data?.message || 'Search failed'),
        }
    );

    const companyOptions = useMemo(
        () =>
            (companies || []).map(company => ({
                label: `${company.code} — ${company.name}`,
                value: company.code,
            })),
        [companies]
    );
    const orderOptions = useMemo(
        () =>
            (orders || []).map(order => ({
                label: order.name,
                value: order._id,
            })),
        [orders]
    );

    const buildFilters = () => {
        const nextFlags = { ...flags };
        if (csvType) nextFlags.csvType = csvType;
        return {
            barcodes: parsed.terms,
            oid: orderId || undefined,
            companyCode: companyCode || undefined,
            flags: nextFlags,
        };
    };

    const clearFilters = () => {
        setBarcodeText('');
        setCompanyCode(undefined);
        setOrderId(undefined);
        setFlags({});
        setCsvType(undefined);
        setSubmitted(null);
        setPageNum(1);
    };

    const runSearch = () => {
        if (!parsed.terms.length && !orderId) {
            message.warning('Enter barcodes or pick a client and order');
            return;
        }
        if (!parsed.terms.length && !companyCode) {
            message.warning('Pick a client, then an order');
            return;
        }
        setPageNum(1);
        setSubmitted(buildFilters());
        setSearchTick(tick => tick + 1);
    };

    const downloadExport = async () => {
        if (!parsed.terms.length && !orderId) {
            message.warning('Search before exporting');
            return;
        }
        setExporting(true);
        try {
            const res = await request({
                url: '/csvdata/export',
                method: 'POST',
                data: buildFilters(),
                responseType: 'blob',
            });
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
            link.setAttribute('download', 'ordercsvdata.csv');
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            message.error(await readError(err, 'Export failed'));
        } finally {
            setExporting(false);
        }
    };

    const openEdit = record => {
        setEditing(record);
        setMappingRows(toRows(record.mapping));
        setPropertyRows(toRows(record.properties));
        setEditFlags({
            useForMapping: record.useForMapping !== false,
            isArticleUpdated: !!record.isArticleUpdated,
            csvType: record.csvType || 'inbound',
        });
    };

    const saveEdit = async () => {
        const mapping = fromRows(mappingRows);
        if (!String(mapping.barcode || '').trim()) {
            message.warning('Barcode is required');
            return;
        }
        setSaving(true);
        try {
            await request({
                url: `/csvdata/${editing._id}`,
                method: 'PATCH',
                data: {
                    mapping,
                    properties: fromRows(propertyRows),
                    csvType: editFlags.csvType,
                    useForMapping: editFlags.useForMapping,
                    isArticleUpdated: editFlags.isArticleUpdated,
                },
            });
            message.success('Saved');
            setEditing(null);
            queryClient.invalidateQueries(['orderCsvSearch']);
        } catch (err) {
            message.error(await readError(err, 'Save failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div style={{ padding: 16 }}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.TextArea
                    value={barcodeText}
                    allowClear
                    autoSize={{ minRows: 1, maxRows: 6 }}
                    placeholder="Barcode, or paste a list"
                    style={{ width: 320 }}
                    onChange={e => setBarcodeText(e.target.value)}
                    onPaste={e => {
                        const text = e.clipboardData?.getData('text') || '';
                        const next = parseSkuSearchList(text, { max: 2000 });
                        if (next.terms.length < 2) return;
                        e.preventDefault();
                        setBarcodeText(next.terms.join('\n'));
                    }}
                />
                <Select
                    showSearch
                    allowClear
                    placeholder="Client"
                    style={{ width: 260 }}
                    loading={companiesLoading}
                    options={companyOptions}
                    optionFilterProp="label"
                    value={companyCode}
                    onChange={value => {
                        setCompanyCode(value);
                        setOrderId(undefined);
                    }}
                />
                <Select
                    showSearch
                    allowClear
                    placeholder="Order"
                    style={{ width: 280 }}
                    disabled={!companyCode}
                    loading={ordersLoading}
                    options={orderOptions}
                    optionFilterProp="label"
                    value={orderId}
                    onChange={setOrderId}
                />
                <Select
                    allowClear
                    placeholder="CSV type"
                    style={{ width: 140 }}
                    options={CSV_TYPES.map(value => ({ label: value, value }))}
                    value={csvType}
                    onChange={setCsvType}
                />
                {FLAG_FILTERS.map(([key, label]) => (
                    <Select
                        key={key}
                        allowClear
                        placeholder={label}
                        style={{ width: 160 }}
                        options={YES_NO}
                        value={flags[key]}
                        onChange={value => setFlags(prev => ({ ...prev, [key]: value }))}
                    />
                ))}
                <Button type="primary" onClick={runSearch}>
                    Search
                </Button>
                <Button onClick={clearFilters}>Clear filters</Button>
                <Button loading={exporting} onClick={downloadExport}>
                    Export
                </Button>
            </Space>
            {parsed.terms.length > 1 ? (
                <div style={{ marginBottom: 8 }}>
                    {parsed.terms.length} barcodes{parsed.truncated ? ' (first 2000)' : ''}
                </div>
            ) : null}
            <Table
                rowKey="_id"
                loading={isFetching}
                dataSource={result?.data || []}
                pagination={{
                    current: pageNum,
                    pageSize: PAGE_SIZE,
                    total: result?.totalCount || 0,
                    showSizeChanger: false,
                    onChange: setPageNum,
                }}
                scroll={{ x: 1100 }}
                expandable={{
                    expandedRowRender: record => (
                        <div>
                            <Descriptions size="small" bordered column={3}>
                                <Descriptions.Item label="Id">{record._id}</Descriptions.Item>
                                <Descriptions.Item label="Order id">{String(record.oid || '')}</Descriptions.Item>
                                <Descriptions.Item label="CSV id">{String(record.csvId || '')}</Descriptions.Item>
                                <Descriptions.Item label="Article id">{String(record.articleId || '')}</Descriptions.Item>
                                <Descriptions.Item label="Created">
                                    {record.createdAt ? dayjs(record.createdAt).format('YYYY-MM-DD HH:mm') : ''}
                                </Descriptions.Item>
                                <Descriptions.Item label="Updated">
                                    {record.updatedAt ? dayjs(record.updatedAt).format('YYYY-MM-DD HH:mm') : ''}
                                </Descriptions.Item>
                            </Descriptions>
                            <KeyValues title="Mapping" obj={record.mapping} />
                            <KeyValues title="Properties" obj={record.properties} />
                            {record.history?.length ? (
                                <div style={{ marginTop: 12 }}>
                                    <div style={{ fontWeight: 600, marginBottom: 8 }}>History</div>
                                    {record.history.map((entry, index) => (
                                        <div key={index}>
                                            {entry.action} · {entry.by || ''} ·{' '}
                                            {entry.at ? dayjs(entry.at).format('YYYY-MM-DD HH:mm') : ''}
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ),
                }}
                columns={[
                    { title: 'Barcode', render: (_, row) => row.mapping?.barcode || '' },
                    { title: 'Client', dataIndex: 'companyCode' },
                    { title: 'Order', dataIndex: 'orderName' },
                    { title: 'CSV type', dataIndex: 'csvType' },
                    { title: 'Consumed', dataIndex: 'isConsumed', render: value => flagTag(value, 'Yes', 'No') },
                    { title: 'Article updated', dataIndex: 'isArticleUpdated', render: value => flagTag(value, 'Yes', 'No') },
                    { title: 'Use for mapping', dataIndex: 'useForMapping', render: value => flagTag(value !== false, 'Yes', 'No') },
                    { title: 'Product variant', dataIndex: 'hasProductVariant', render: value => flagTag(value, 'Yes', 'No') },
                    {
                        title: '',
                        width: 90,
                        render: (_, row) =>
                            row.isConsumed ? null : (
                                <Button size="small" onClick={() => openEdit(row)}>
                                    Edit
                                </Button>
                            ),
                    },
                ]}
            />
            <Modal
                title={editing ? `Edit ${editing.mapping?.barcode || ''}` : 'Edit'}
                open={!!editing}
                onCancel={() => setEditing(null)}
                onOk={saveEdit}
                confirmLoading={saving}
                width={760}
                destroyOnClose
            >
                <Space style={{ marginBottom: 12 }}>
                    <span>Use for mapping</span>
                    <Switch
                        checked={editFlags.useForMapping}
                        onChange={checked => setEditFlags(prev => ({ ...prev, useForMapping: checked }))}
                    />
                    <span>Article updated</span>
                    <Switch
                        checked={editFlags.isArticleUpdated}
                        onChange={checked => setEditFlags(prev => ({ ...prev, isArticleUpdated: checked }))}
                    />
                    <Input
                        value={editFlags.csvType}
                        style={{ width: 140 }}
                        onChange={e => setEditFlags(prev => ({ ...prev, csvType: e.target.value }))}
                    />
                </Space>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>Mapping</div>
                <RowEditor rows={mappingRows} onChange={setMappingRows} lockKeys={['barcode']} />
                <div style={{ fontWeight: 600, margin: '12px 0 8px' }}>Properties</div>
                <RowEditor rows={propertyRows} onChange={setPropertyRows} />
            </Modal>
        </div>
    );
}
